import { makeAutoObservable, action, observable, runInAction } from 'mobx';
import { trackAddToCart } from '@/utils/tracking';
import api from '@/utils/api';
import { fetcher, getProductsKey } from "@/utils/fetchers";

class WooCommerceStore {
  generalSettings = [];
  productSettings = [];
  cart = [];
  cartVersion = '1';
  isProductModalOpen = false;
  selectedProduct = null;
  isCartOpen = false;
  isMinOrderModalOpen = false;
  isPerformingPayment = false;
  isShippingAreaCheckModalOpen = false;
  isCartSyncModalOpen = false;
  cartSyncData = null;
  quantityError = null;

  cartSubscribers = [];
  lastModifiedItem = null;
  lastModificationType = null; // 'add', 'update', 'remove'

  categoryProducts = new Map(); // Map<categoryId, products[]>
  categoryLoadingStates = new Map(); // Map<categoryId, {currentPage, hasMore, loading}>
  productModalData = null;
  activeCategoryId = null;
  activeFilters = { tagIds: [], onSaleOnly: false };
  loadingNextCategoryId = null;
  userHasScrolled = false;

  constructor() {
    makeAutoObservable(this, {
      setProductSettings: action,
      setGeneralSettings: action,
      addToCart: action,
      removeFromCart: action,
      updateCartItemQuantity: action,
      loadCartFromLocalStorage: action,
      saveCartToLocalStorage: action,
      showProductModal: action,
      hideProductModal: action,
      setCart: action,
      toggleCart: action,
      cartSubscribers: false,
      lastModifiedItem: observable,
      lastModificationType: observable,
      isCartOpen: observable,
      isMinOrderModalOpen: observable,
      setPerformingPayment: action,
      isPerformingPayment: observable,
      isCartSyncModalOpen: observable,
      cartSyncData: observable,
      quantityError: observable,
      activeCategoryId: observable,
      activeFilters: observable,
      loadingNextCategoryId: observable,
      userHasScrolled: observable,
    });

    this.loadCartFromLocalStorage();
  }

  initializeCategoryState(categoryId, initialProducts) {
    if (!this.categoryProducts.has(categoryId)) {
      this.categoryProducts.set(categoryId, initialProducts);
      this.categoryLoadingStates.set(categoryId, {
        currentPage: 1,
        hasMore: true,
        loading: false,
      });
    }
  }

  async loadMoreProducts(categoryId, getProductsKeyFn) {
    const state = this.categoryLoadingStates.get(categoryId);
    if (!state || state.loading || !state.hasMore) return;

    runInAction(() => {
      this.categoryLoadingStates.set(categoryId, { ...state, loading: true });
    });

    try {
      const keyFn = getProductsKeyFn || getProductsKey;
      const data = await fetcher(keyFn(state.currentPage + 1, categoryId, 15));

      runInAction(() => {
        if (data?.products?.length) {
          const currentProducts = this.categoryProducts.get(categoryId) || [];
          this.categoryProducts.set(categoryId, [...currentProducts, ...data.products]);

          const hasMore = data.products.length === 15;
          this.categoryLoadingStates.set(categoryId, {
            currentPage: state.currentPage + 1,
            hasMore,
            loading: false,
          });
        } else {
          this.categoryLoadingStates.set(categoryId, {
            ...state,
            hasMore: false,
            loading: false,
          });
        }
      });

      return data?.products?.length === 15;
    } catch (error) {
      console.error("Error loading more products:", error);
      runInAction(() => {
        this.categoryLoadingStates.set(categoryId, {
          ...state,
          hasMore: false,
          loading: false,
        });
      });
      return false;
    }
  }

  getFilteredProducts(categoryId, selectedTag, onSaleOnly) {
    const products = this.categoryProducts.get(categoryId) || [];
    if (!selectedTag && !onSaleOnly) return products;

    return products.filter((product) => {
      const hasSelectedTag = selectedTag
        ? selectedTag.split(',').some(tagId => {
            const productTagIds = (product.tags || []).map(tag => tag.id.toString());
            return productTagIds.includes(tagId.toString());
          })
        : true;

      const isOnSale = onSaleOnly ? product.on_sale : true;
      return hasSelectedTag && isOnSale;
    });
  }

  clearCategoryData(categoryId) {
    this.categoryProducts.delete(categoryId);
    this.categoryLoadingStates.delete(categoryId);
  }

  showProductModal(product) {
    this.isProductModalOpen = true;
    this.selectedProduct = product;
    this.productModalData = product;
  }

  hideProductModal() {
    this.isProductModalOpen = false;
    this.selectedProduct = null;
    this.productModalData = null;
  }

  setProductSettings(settings) {
    this.productSettings = settings;
  }

  setGeneralSettings(settings) {
    this.generalSettings = settings;
  }

  get currencySymbol() {
    return this.generalSettings?.currency_symbol || '₪'; // Default to ILS symbol
  }

  get weightUnit() {
    return this.generalSettings?.weight_unit || 'kg'; // Default to kg
  }

  get currency() {
    return this.generalSettings?.currency || 'ILS'; // Default to ILS
  }

  get currencyPos() {
    return this.generalSettings?.currency_pos || 'left';
  }

  get numDecimals() {
    return this.generalSettings?.price_num_decimals || 2;
  }

  get decimalSep() {
    return this.generalSettings?.price_decimal_sep || '.';
  }

  get thousandSep() {
    return this.generalSettings?.price_thousand_sep || ',';
  }

  // Cart Management
  addToCart(product, quantity = 1) {
    const existingItem = this.cart.find(item => item.id === product.id);
    const maxQuantity = product.max_cart_quantity ? parseInt(product.max_cart_quantity) : null;

    if (maxQuantity !== null) {
      const newTotalQuantity = (existingItem?.quantity || 0) + quantity;
      if (newTotalQuantity > maxQuantity) {
        this.quantityError = { key: 'max_quantity_error', values: { quantity: maxQuantity } };
        setTimeout(() => {
          this.quantityError = null;
        }, 3000);
        return false;
      }
    }

    if (existingItem) {
      existingItem.quantity += quantity;
      this.lastModifiedItem = { ...existingItem };
      this.lastModificationType = 'update';
    } else {
      const newItem = { ...product, quantity };
      this.cart.push(newItem);
      this.lastModifiedItem = newItem;
      this.lastModificationType = 'add';

      // Track add to cart event
      trackAddToCart(product, quantity);

      // If this is the first item and user is not logged in and hasn't checked shipping area before
      if (this.cart.length === 1 &&
          !localStorage.getItem('token') &&
          !localStorage.getItem('shipping_area_checked')) {
        this.toggleShippingAreaCheckModal();
      }
    }
    this.saveCartToLocalStorage();
    return true;
  }

  removeFromCart(productId) {
    const item = this.cart.find(item => item.id === productId);
    if (item) {
      this.lastModifiedItem = { ...item };
      this.lastModificationType = 'remove';
    }
    this.cart = this.cart.filter(item => item.id !== productId);
    this.saveCartToLocalStorage();
  }

  updateCartItemQuantity(productId, quantity) {
    const item = this.cart.find(item => item.id === productId);
    if (item) {
      const maxQuantity = item.max_cart_quantity ? parseInt(item.max_cart_quantity) : null;

      if (maxQuantity !== null && quantity > maxQuantity) {
        this.quantityError = { key: 'max_quantity_error', values: { quantity: maxQuantity } };
        setTimeout(() => {
          this.quantityError = null;
        }, 3000);
        return false;
      }

      item.quantity = quantity;
      if (item.quantity <= 0) {
        this.removeFromCart(productId);
      } else {
        this.lastModifiedItem = { ...item };
        this.lastModificationType = 'update';
        this.saveCartToLocalStorage();
      }
      return true;
    }
    return false;
  }

  updateCartItemNote(productId, note) {
    const item = this.cart.find(item => item.id === productId);
    if (item) {
      item.note = note;
      this.lastModifiedItem = { ...item };
      this.lastModificationType = 'update';
      this.saveCartToLocalStorage();
    }
  }

  clearCart() {
    this.cart = [];
    this.lastModifiedItem = null;
    this.lastModificationType = 'clear';
    this.saveCartToLocalStorage();
  }

  loadCartFromLocalStorage() {
    if (typeof window !== "undefined") {
      const savedCart = JSON.parse(localStorage.getItem('cart')) || [];
      this.cart = savedCart;
    }
  }

  saveCartToLocalStorage() {
    if (typeof window !== "undefined") {
      const sortedCart = this.cart.sort((a, b) => {
        // Get the first category name for each item
        const categoryA = a.categories && a.categories.length > 0 ? a.categories[0].name.toLowerCase() : '';
        const categoryB = b.categories && b.categories.length > 0 ? b.categories[0].name.toLowerCase() : '';

        // Compare category names
        if (categoryA < categoryB) return -1;
        if (categoryA > categoryB) return 1;
        return 0;
      });
      localStorage.setItem('cart', JSON.stringify(sortedCart));
      console.log('sortedCart', sortedCart);
      this.cartVersion = Math.floor(Math.random() * 1000) + 1;

      // Notify subscribers
      this.cartSubscribers.forEach(callback => callback());
    }
  }

  subscribeToCart(callback) {
    this.cartSubscribers.push(callback);
    return () => {
      const index = this.cartSubscribers.indexOf(callback);
      if (index > -1) {
        this.cartSubscribers.splice(index, 1);
      }
    };
  }

  getCartTotal() {
    return this.cart.reduce((total, item) => total + item.price * item.quantity, 0);
  }

  getCartItemCount() {
    return this.cart.reduce((total, item) => total + item.quantity, 0);
  }

  setCart(newCart) {
    this.cart = newCart;
    this.saveCartToLocalStorage();
  }

  toggleCart = () => {
    this.isCartOpen = !this.isCartOpen;
  }

  toggleMinOrderModal = () => {
    this.isMinOrderModalOpen = !this.isMinOrderModalOpen;
  }

  setPerformingPayment = (value) => {
    this.isPerformingPayment = value;
  }

  toggleShippingAreaCheckModal = () => {
    this.isShippingAreaCheckModalOpen = !this.isShippingAreaCheckModalOpen;
  }

  toggleCartSyncModal = (syncData = null) => {
    this.isCartSyncModalOpen = !this.isCartSyncModalOpen;
    this.cartSyncData = syncData;
  }

  setCartSyncModalOpen = (open) => {
    this.isCartSyncModalOpen = open;
  }

  setCartSyncData = (syncData) => {
    this.cartSyncData = syncData;
  }

  /**
   * Save the current cart to the database
   * @param {string} name - The name to give the saved cart
   * @param {boolean} share - Whether the cart should be shared
   * @returns {Promise} - The API response
   */
  saveCart = async (name, share = false) => {
    try {
      // Prepare the cart data
      const cartData = {
        name: name,
        is_shared: share ? 1 : 0,
        items: this.cart.map(item => ({
          product_id: item.type === "variation" ? item.parent_id : item.id,
          variation_id: item.type === "variation" ? item.id : 0,
          quantity: item.quantity,
        })),
      };

      // Send the request to the API
      const response = await api.post('/carts', cartData);
      return response.data;
    } catch (error) {
      console.error('Error saving cart:', error);
      throw error;
    }
  };

  /**
   * Load a saved cart from the database
   * @param {number} cartId - The ID of the cart to load
   * @returns {Promise} - The API response
   */
  loadCart = async (cartId) => {
    try {
      // Get the cart from the API
      const response = await api.get(`/carts/${cartId}`);

      if (response.data && response.data.items) {
        // Clear the current cart
        this.clearCart();

        // Add each item to the cart
        for (const item of response.data.items) {
          try {
            if (!item.product_id) {
              continue;
            }

            // Fetch current product data
            const productResponse = await fetch(`/api/shop/product/${item.product_id}`);
            const product = await productResponse.json();

            if (item.variation_id && item.variation_id !== 0) {
              // For variable products, fetch the variation separately
              const variationResponse = await fetch(`/api/shop/product/variation/${item.variation_id}?productId=${item.product_id}`);
              const variation = await variationResponse.json();

              if (variation && variation.status === 'publish' && variation.stock_status === 'instock') {
                const variationProduct = {
                  ...variation,
                  parent_id: item.product_id,
                  parent_name: product.name,
                  type: 'variable',
                  price: variation.price,
                  regular_price: variation.regular_price,
                  sale_price: variation.sale_price,
                };
                this.addToCart(variationProduct, item.quantity);
              }
            } else {
              // For simple products
              if (product && product.status === 'publish' && product.stock_status === 'instock') {
                this.addToCart(product, item.quantity);
              }
            }
          } catch (error) {
            console.error(`Error fetching product ${item.product_id}:`, error);
          }
        }

        return response.data;
      }
    } catch (error) {
      console.error('Error loading cart:', error);
      throw error;
    }
  };

  /**
   * Get all saved carts for the current user
   * @returns {Promise} - The API response
   */
  getSavedCarts = async () => {
    try {
      const response = await api.get('/carts');
      return response.data;
    } catch (error) {
      console.error('Error getting saved carts:', error);
      throw error;
    }
  };

  /**
   * Delete a saved cart
   * @param {number} cartId - The ID of the cart to delete
   * @returns {Promise} - The API response
   */
  deleteCart = async (cartId) => {
    try {
      const response = await api.delete(`/carts/${cartId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting cart:', error);
      throw error;
    }
  };

  setActiveCategoryId(categoryId) {
    this.activeCategoryId = categoryId;
  }

  setActiveFilters(filters) {
    this.activeFilters = filters;
  }

  setLoadingNextCategoryId(categoryId) {
    this.loadingNextCategoryId = categoryId;
  }

  setUserHasScrolled(hasScrolled) {
    this.userHasScrolled = hasScrolled;
  }

  // Add this method to handle scroll detection
  handleScroll(isScrolling) {
    if (!isScrolling) {
      this.setUserHasScrolled(true);
    }
  }

  // Method to reset category navigation state
  resetCategoryNavigation() {
    this.activeCategoryId = null;
    this.loadingNextCategoryId = null;
    this.userHasScrolled = false;
    this.activeFilters = { tagIds: [], onSaleOnly: false };
  }

  async loadAllProductsForCategory(categoryId, getProductsKeyFn) {
    const state = this.categoryLoadingStates.get(categoryId);
    if (!state || !state.hasMore) {
      return true; // Already fully loaded
    }

    try {
      let keepLoading = true;
      while (keepLoading) {
        const hasMore = await this.loadMoreProducts(categoryId, getProductsKeyFn);
        if (!hasMore) {
          keepLoading = false;
        }
      }
      return true;
    } catch (error) {
      console.error(`Error loading all products for category ${categoryId}:`, error);
      return false;
    }
  }
}

const wooCommerceStore = new WooCommerceStore();
export default wooCommerceStore;
